import * as React from 'react';
import * as _ from 'lodash';
import {
  Certificate,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  CustomerSummary,
  DisplayData,
  Footer,
  Logging,
  renderEdit,
  renderSelect,
  Notification,
  CertificateClass,
  CertificateScope,
  Reason,
  Product,
  renderDate,
  stringToDate,
  formatDate,
  DateFormatEnum,
  settingContextObjectLookup,
  settingCertificateCustomField,
  SettingsContextObject,
  TaxResult,
  TransactionType,
  Seller,
  jsJodaLocalDateToJavascriptDate,
} from '@vcd-ecm-ui/Common';
import useCustomers from '../Common/useCustomers';
import useCustomer from '../Common/useCustomer';
import useCertificateClasses from '../Common/useCertificateClasses';
import CertificateInformationProps from './CertificateInformationProps.interface';
import useCertificateScopes from '../Common/useCertificateScopes';
import '../steps.scss';
import useExemptionReasons from '../Common/useExemptionReason';
import useTaxResults from '../Common/useTaxResults';
import useTransactionTypes from '../Common/useTransactionTypes';
import useSellers from '../Common/useSellers';
import useProducts from '../Common/useProducts';
import SelectedItems from '../../Common/selectedItems';

export interface SearchAhead {
  searching: boolean;
  onSearch: (searchValue: string) => void;
  searchingText: string;
  noResultsText: string;
  debounceMilliseconds: number;
}

const scopeSingleUse = 'Single Use';
const scopeLimited = 'Limited';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: [
    'customer',
    'certificateClass',
    'exemptionReason',
    'certificateScope',
    'documentNumber',
    'products',
    'startDate',
    'endDate',
    'taxResult',
    'transactionType',
  ],
  customMessages: [],
};

const CertificateInformation = ({
  gatewayEndPoint,
  step,
  certificate,
  selectedItems,
  onCancel,
  onContinuteSave,
}: CertificateInformationProps): JSX.Element => {
  Logging.Debug('Certificate Information', certificate);

  // Get the Product Objects - String [] of JSOn Serialized Products
  const getProductObjects = (): string[] => {
    if (selectedItems?.products) {
      // eslint-disable-next-line arrow-body-style
      return selectedItems?.products.map((product: Product) => {
        return JSON.stringify(product);
      });
    }

    return [];
  };

  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [notification, setNotification] = React.useState(null);

  const [customerUuid, setCustomerUuid] = React.useState(selectedItems?.customer?.uuid);
  const [certificateClass, setCertificateClass] = React.useState(certificate?.className);
  const [exemptionReasonObject, setExemptionReasonObject] = React.useState(selectedItems?.exemptionReason
    ? JSON.stringify(selectedItems?.exemptionReason) : null);
  const [certificateScope, setCertificateScope] = React.useState(certificate?.certificateScope);
  const [documentNumber, setDocumentNumber] = React.useState(certificate?.documentNumber?.toString());
  // Whole Product Object is stored because of a Type Ahead Search for Products and that user may come back to previous Step
  const [productObjects, setProductObjects] = React.useState(getProductObjects());
  const [startDate, setStartDate] = React.useState(stringToDate(certificate?.effectiveDate));
  // Whole Seller Object is stored because of a Type Ahead Search for Sellers and that user may come back to previous Step
  const [sellerObject, setSellerObject] = React.useState(selectedItems?.seller
    ? JSON.stringify(selectedItems?.seller) : null);
  const [endDate, setEndDate] = React.useState(stringToDate(certificate?.endDate));
  const [taxResult, setTaxResult] = React.useState(null); // FIX ME - Get Tax Result
  const [transactionType, setTransactionType] = React.useState([]); // FIX ME - Get Transaction Types
  const [notes, setNotes] = React.useState(certificate?.notes);
  const [customField1, setCustomField1] = React.useState(certificate?.customField1);
  const [customField2, setCustomField2] = React.useState(certificate?.customField2);
  const [customField3, setCustomField3] = React.useState(certificate?.customField3);
  const [customField4, setCustomField4] = React.useState(certificate?.customField4);
  const [customField5, setCustomField5] = React.useState(certificate?.customField5);
  const [customField6, setCustomField6] = React.useState(certificate?.customField6);
  const [customField7, setCustomField7] = React.useState(certificate?.customField7);
  const [customField8, setCustomField8] = React.useState(certificate?.customField8);
  const [customField9, setCustomField9] = React.useState(certificate?.customField9);
  const [customField10, setCustomField10] = React.useState(certificate?.customField10);

  // #region Customer

  // Search Customer Text
  const [searchCustomer, setSearchCustomer] = React.useState(null);

  // Searched Customers
  const [customers, searchingCustomers, customersNotification] = useCustomers(gatewayEndPoint, searchCustomer);

  // Load the Selected Customer.  If Customer was selected before, use it
  const [selectedCustomer, customerNotification] = selectedItems?.customer
    ? [selectedItems?.customer, null]
    : useCustomer(gatewayEndPoint, customerUuid);

  // Customer Search Value changed
  const searchCustomers = (searchValue: string) => {
    Logging.Debug('Searching Customers', searchValue);
    setSearchCustomer(searchValue);
  };

  // Get the Customers - Format to display: Name (Code)
  const getCustomers = (): DisplayData[] => {
    if (customers) {
      // eslint-disable-next-line arrow-body-style
      return customers.map((item: CustomerSummary) => {
        return {
          data: item.customerUuid,
          display: `${item.customerName} (${item.customerCode})`,
        };
      });
    }

    // Use the Selected Customer - user has come back to Step 1
    if (selectedItems?.customer) {
      return [{
        data: selectedItems?.customer.uuid,
        display: `${selectedItems?.customer.name} (${selectedItems?.customer.code})`,
      }];
    }

    return [];
  };

  // Customer Search Ahead
  const searchCustomerAhead: SearchAhead = {
    searching: searchingCustomers,
    searchingText: 'Searching Customers ...',
    noResultsText: 'No Customers found',
    onSearch: searchCustomers,
    debounceMilliseconds: 750,
  };

  // #endregion

  // #region Certificate Class

  // Certificate Classes
  const [certificateClasses, certificateClassesNotification] = useCertificateClasses(gatewayEndPoint);

  const getCertificateClasses = (): DisplayData[] => {
    if (certificateClasses) {
      // eslint-disable-next-line arrow-body-style
      return certificateClasses.map((item: CertificateClass) => {
        return {
          data: item.name,
          display: item.name,
        };
      });
    }

    return [];
  };

  // #endregion

  // #region Exemption Reason

  // Exemption Reasons
  const [exemptionReasons, exemptionReasonsNotification] = useExemptionReasons(gatewayEndPoint);

  const getExemptionReasons = (): DisplayData[] => {
    if (exemptionReasons) {
      // eslint-disable-next-line arrow-body-style
      return exemptionReasons.map((item: Reason) => {
        return {
          data: JSON.stringify(item), // Data is JSON Serialized Exemption Reason object
          display: item.type,
        };
      });
    }

    return [];
  };

  // #endregion

  // #region Certificate Scope

  // Certificate Scope
  const [certificateScopes, certificateScopesNotification] = useCertificateScopes(gatewayEndPoint);

  const getCertificateScopes = (): DisplayData[] => {
    if (certificateScopes) {
      // eslint-disable-next-line arrow-body-style
      return certificateScopes.map((item: CertificateScope) => {
        return {
          data: item.name,
          display: item.name,
        };
      });
    }

    return [];
  };

  // #endregion

  // #region Seller

  // Search Seller Text
  const [searchSeller, setSearchSeller] = React.useState(null);

  // Searched Sellers
  const [sellers, searchingSellers, sellersNotification] = useSellers(gatewayEndPoint, searchSeller);

  // Seller Search Value changed
  const searchSellers = (searchValue: string) => {
    Logging.Debug('Searching Sellers', searchValue);
    setSearchSeller(searchValue);
  };

  // Get the Seller - Format to display: Name (Code)
  const getSellers = (): DisplayData[] => {
    if (sellers) {
      // eslint-disable-next-line arrow-body-style
      return sellers.map((item: Seller) => {
        return {
          data: JSON.stringify(item), // Data is JSON Serialized Seller object
          display: `${item.name} (${item.code})`,
        };
      });
    }

    // Use the Selected Seller - User has come back to Step 1
    if (sellerObject) {
      // Get the Seller
      const seller: Seller = JSON.parse(sellerObject);

      return [{
        data: sellerObject, // // Data is JSON Serialized Seller object
        display: `${seller?.name} (${seller?.code})`,
      }];
    }

    return [];
  };

  // Seller Search Ahead
  const searchSellerAhead: SearchAhead = {
    searching: searchingSellers,
    searchingText: 'Searching Sellers ...',
    noResultsText: 'No Sellers found',
    onSearch: searchSellers,
    debounceMilliseconds: 750,
  };

  // #endregion

  // #region Products

  // Search Product Text
  const [searchProduct, setSearchProduct] = React.useState(null);

  // Searched Products
  const [products, searchingProducts, productsNotification] = useProducts(gatewayEndPoint, searchProduct);

  // Get the Products
  const getProducts = (): DisplayData[] => {
    if (products) {
      // eslint-disable-next-line arrow-body-style
      return products.map((item: Product) => {
        return {
          data: JSON.stringify(item), // Data is JSON Serialized Product object
          display: item.name,
        };
      });
    }

    // Use the Selected Products - User has come back to Step 1
    if (productObjects) {
      // eslint-disable-next-line arrow-body-style
      return productObjects.map((productObject: string) => {
        // Get the Product
        const product: Product = JSON.parse(productObject);

        return {
          data: productObject, // Data is JSON Serialized Product object
          display: product.name,
        };
      });
    }

    return [];
  };

  // Seller Search Value changed
  const searchProducts = (searchValue: string) => {
    Logging.Debug('Searching Products', searchValue);
    setSearchProduct(searchValue);
  };

  // Product Search Ahead
  const searchProductAhead: SearchAhead = {
    searching: searchingProducts,
    searchingText: 'Searching Products ...',
    noResultsText: 'No Products found',
    onSearch: searchProducts,
    debounceMilliseconds: 750,
  };

  // #endregion

  // #region Tax Results

  // Tax Results
  const [taxResults, taxResultsNotification] = useTaxResults(gatewayEndPoint);

  const getTaxResults = (): DisplayData[] => {
    if (taxResults) {
      // eslint-disable-next-line arrow-body-style
      return taxResults.map((item: TaxResult) => {
        return {
          data: item.name,
          display: item.name,
        };
      });
    }

    return [];
  };

  // #endregion

  // #region Transaction Types

  // Transaction Types
  const [transactionTypes, transactionTypesNotification] = useTransactionTypes(gatewayEndPoint);

  const getTransactionTypes = (): DisplayData[] => {
    if (transactionTypes) {
      // eslint-disable-next-line arrow-body-style
      return transactionTypes.map((item: TransactionType) => {
        return {
          data: item.name,
          display: item.name,
        };
      });
    }

    return [];
  };

  // #endregion

  // #region Custom Fields

  const customFieldHeader1: string = settingContextObjectLookup(settingsContext.certificates, 1, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader2: string = settingContextObjectLookup(settingsContext.certificates, 2, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader3: string = settingContextObjectLookup(settingsContext.certificates, 3, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader4: string = settingContextObjectLookup(settingsContext.certificates, 4, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader5: string = settingContextObjectLookup(settingsContext.certificates, 5, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader6: string = settingContextObjectLookup(settingsContext.certificates, 6, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader7: string = settingContextObjectLookup(settingsContext.certificates, 7, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader8: string = settingContextObjectLookup(settingsContext.certificates, 8, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader9: string = settingContextObjectLookup(settingsContext.certificates, 9, settingCertificateCustomField, 'Custom Field');
  const customFieldHeader10: string = settingContextObjectLookup(settingsContext.certificates, 10, settingCertificateCustomField, 'Custom Field');

  // #endregion

  // #region Validate & Continue

  // Continue / Save Button
  const onSaveContinue = (): void => {
    const copyCertificate: Certificate = _.cloneDeep(certificate);
    const copySelectedItems: SelectedItems = _.cloneDeep(selectedItems);

    // Get the Selected Seller
    const selectedSeller: Seller = sellerObject ? JSON.parse(sellerObject) : null;

    // Get the Selected Exemption Reason
    const selectedExemptionReason: Reason = exemptionReasonObject ? JSON.parse(exemptionReasonObject) : null;

    // Get the Selected Products
    const selectedProducts: Product[] = productObjects?.map((productObject: string) => { // eslint-disable-line arrow-body-style
      return JSON.parse(productObject);
    });

    // Update the Selected Items
    copySelectedItems.customer = selectedCustomer;
    copySelectedItems.seller = selectedSeller;
    copySelectedItems.products = selectedProducts;
    copySelectedItems.exemptionReason = selectedExemptionReason;

    // Update the Certificate Customer
    copyCertificate.customer = {
      uuid: selectedCustomer.uuid,
      code: selectedCustomer.code,
      name: selectedCustomer.name,
      effectiveDate: selectedCustomer.effectiveDate,
      endDate: selectedCustomer.endDate,
      isParentInd: selectedCustomer.parent !== null,
    };

    // Update the Certificate Class
    copyCertificate.className = certificateClass;

    // Update the Certifcate Scope
    copyCertificate.certificateScope = certificateScope;

    // Update the Document Number
    copyCertificate.documentNumber = (certificateScope === scopeSingleUse) ? Number(documentNumber) : null;

    // Update the Products
    copyCertificate.products = selectedProducts;

    // Update the Seller
    // copyCertificate.seller = selectedSeller; // FIX ME: CertificateSeller does not match Seller

    // Start Date
    copyCertificate.effectiveDate = formatDate(startDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' });

    // End Date
    copyCertificate.endDate = formatDate(endDate, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' });

    // Tax Result
    // copyCertificate.taxResult = taxResult; // FIX ME

    // Update the Transaction Types // FIX ME
    // copyCertificate.transactionTypes = transactionTypes.filter((transaction: TransactionType) => transactionType.includes(transaction.name, 0));

    // Notes
    copyCertificate.notes = notes;

    // Custom Fields
    copyCertificate.customField1 = customField1;
    copyCertificate.customField2 = customField2;
    copyCertificate.customField3 = customField3;
    copyCertificate.customField4 = customField4;
    copyCertificate.customField5 = customField5;
    copyCertificate.customField6 = customField6;
    copyCertificate.customField7 = customField7;
    copyCertificate.customField8 = customField8;
    copyCertificate.customField9 = customField9;
    copyCertificate.customField10 = customField10;

    Logging.Info('Continue to Step 2:', copyCertificate, selectedItems);

    onContinuteSave(step, copyCertificate, copySelectedItems);
  };

  // Validate the Step
  const validateStep = (): boolean => {
    // Customer is Required
    if (!customerUuid) {
      return false;
    }

    // Certificate Class is Required
    if (!certificateClass) {
      return false;
    }

    // Exemption Reason is Required
    if (!exemptionReasonObject) {
      return false;
    }

    // Certificate Scope is Required
    if (!certificateScope) {
      return false;
    }

    if (certificateScope === scopeSingleUse) {
      // Document Number is Required
      if (!documentNumber) {
        return false;
      }
    }

    if (certificateScope === scopeLimited) {
      // Products is Required
      if (!productObjects || (productObjects.length === 0)) {
        return false;
      }
    }

    // Start Date is Required
    if (!startDate) {
      return false;
    }

    // End Date is Required
    if (!endDate) {
      return false;
    }

    // Validate against Customer Start Date
    if (selectedCustomer && selectedCustomer.effectiveDate) {
      const customerStartDate = stringToDate(selectedCustomer.effectiveDate);

      if (startDate && (startDate < customerStartDate)) {
        return false;
      }
    }

    // Validate against Customer End Date
    if (selectedCustomer && selectedCustomer.endDate) {
      const customerEndDate = stringToDate(selectedCustomer.endDate);

      if (startDate && (startDate > customerEndDate)) {
        return false;
      }

      if (endDate && (endDate > customerEndDate)) {
        return false;
      }
    }

    if (startDate && endDate && (startDate > endDate)) {
      return false;
    }

    // Tax Result is Required
    if (!taxResult) {
      return false;
    }

    // Transaction Type is Required
    if (!transactionType || (transactionType.length === 0)) {
      return false;
    }

    return true;
  };

  // Clear the Custom Messages
  controlValidation.customMessages = [];

  // Validate against Selected Customer Start Date
  if (selectedCustomer && selectedCustomer.effectiveDate) {
    const customerStartDate = stringToDate(selectedCustomer.effectiveDate);

    Logging.Debug('Customer Start Date:', customerStartDate, startDate, endDate);

    if (startDate && (startDate < customerStartDate)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'startDate', message: `Start cannot be before Customer Start (${selectedCustomer.effectiveDate})`, type: 'Error' }];
    }

    if (endDate && (endDate < customerStartDate)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'endDate', message: `End cannot be before Customer Start (${selectedCustomer.effectiveDate})`, type: 'Error' }];
    }
  }

  // Validate against Selected Customer End Date
  if (selectedCustomer && selectedCustomer.endDate) {
    const customerEndDate = stringToDate(selectedCustomer.endDate);

    Logging.Debug('Customer End Date:', customerEndDate, endDate);

    if (startDate && (startDate > customerEndDate)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'startDate', message: `Start cannot be after Customer End (${selectedCustomer.endDate})`, type: 'Error' }];
    }

    if (endDate && (endDate > customerEndDate)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        // eslint-disable-next-line max-len
        { field: 'endDate', message: `End cannot be after Customer End (${selectedCustomer.endDate})`, type: 'Error' }];
    }
  }

  if (startDate && endDate && (startDate > endDate)) {
    controlValidation.customMessages = [...controlValidation.customMessages,
      // eslint-disable-next-line max-len
      { field: 'endDate', message: '`End Date cannot be before Start Date', type: 'Error' }];
  }

  const canSaveContinue = true; // validateStep();

  // #endregion

  // #region Notification

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  // Set the Error Notification
  React.useEffect(() => {
    setNotification(customerNotification
      || customersNotification
      || certificateClassesNotification
      || certificateScopesNotification
      || taxResultsNotification
      || transactionTypesNotification
      || sellersNotification
      || productsNotification);
  }, [customerNotification, customersNotification, certificateClassesNotification,
    certificateScopesNotification, taxResultsNotification, transactionTypesNotification, sellersNotification, productsNotification]);

  // #endregion

  // Determine if Start Date must be enabled
  const disabledStartDate = (date: Date): boolean => {
    if (selectedCustomer && selectedCustomer.effectiveDate) {
      const customerStartDate = stringToDate(selectedCustomer.effectiveDate);

      if (date < customerStartDate) {
        return true;
      }
    }

    if (selectedCustomer && selectedCustomer.endDate) {
      const customerEndDate = stringToDate(selectedCustomer.endDate);

      if (date > customerEndDate) {
        return true;
      }
    }

    return false;
  };

  // Determine if End Date must be enabled
  const disabledEndDate = (date: Date): boolean => {
    if (selectedCustomer && selectedCustomer.effectiveDate) {
      const customerStartDate = stringToDate(selectedCustomer.effectiveDate);

      if (date < customerStartDate) {
        return true;
      }
    }

    if (selectedCustomer && selectedCustomer.endDate) {
      const customerEndDate = stringToDate(selectedCustomer.endDate);

      if (date > customerEndDate) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <div className="stepContent">
        {renderSelect(50, 'customer', 'Customer*', 'Search by Customer Code or Name', false, false,
          getCustomers(), true, searchCustomerAhead, customerUuid, setCustomerUuid, controlStateEditable, controlStyle, controlValidation)}
        {renderSelect(50, 'certificateClass', 'Class*', 'Select a Certificate Class', false, true,
          getCertificateClasses(), true, null, certificateClass, setCertificateClass, controlStateEditable, controlStyle, controlValidation)}
        {renderSelect(50, 'exemptionReason', 'Reason*', 'Select an Exemption Reason', false, true,
          getExemptionReasons(), true, null, exemptionReasonObject, setExemptionReasonObject, controlStateEditable, controlStyle, controlValidation)}
        {renderSelect(50, 'certificateScope', 'Certificate Scope*', 'Select a Certificate Scope', false, true,
          getCertificateScopes(), true, null, certificateScope, setCertificateScope, controlStateEditable, controlStyle, controlValidation)}
        {renderSelect(50, 'seller', 'Seller', 'Search by Seller Code or Name', false, false,
          getSellers(), true, searchSellerAhead, sellerObject, setSellerObject, controlStateEditable, controlStyle, controlValidation)}
        {
          certificateScope === scopeSingleUse
            ? (
              <>
                {renderEdit(50, 'documentNumber', 'Document Number*', 'number', 'Document Number',
                  documentNumber, setDocumentNumber, controlStateEditable, controlStyle, controlValidation)}
              </>
            )
            : (
              <div className="field50" />
            )
        }
        {
          certificateScope === scopeLimited && (
            <>
              {renderSelect(100, 'products', 'Product(s)*', 'Search by Product Name', true, true,
                getProducts(), true, searchProductAhead, productObjects, setProductObjects, controlStateEditable, controlStyle, controlValidation)}
            </>
          )
        }
        {renderDate(50, 'startDate', 'Start Date*', 'Select a Start Date', startDate, setStartDate, disabledStartDate, controlStateEditable,
          controlStyle, controlValidation)}
        {renderDate(50, 'endDate', 'End Date*', 'Select an End Date', endDate, setEndDate, disabledEndDate, controlStateEditable,
          controlStyle, controlValidation)}
        {renderSelect(50, 'taxResult', 'Tax Result*', 'Select a Tax Result', false, true,
          getTaxResults(), true, null, taxResult, setTaxResult, controlStateEditable, controlStyle, controlValidation)}
        {renderSelect(50, 'transactionType', 'Transaction Type(s)*', 'Select Transaction Type(s)', true, true,
          getTransactionTypes(), true, null, transactionType, setTransactionType, controlStateEditable, controlStyle, controlValidation)}

        {renderEdit(100, 'notes', 'Notes', 'text', 'Notes',
          notes, setNotes, controlStateEditable, controlStyle, controlValidation)}

        {
          customFieldHeader1
          && (
            renderEdit(50, 'customfield-1', customFieldHeader1, 'text', null, customField1, setCustomField1, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader2
          && (
            renderEdit(50, 'customfield-2', customFieldHeader2, 'text', null, customField2, setCustomField2, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader3
          && (
            renderEdit(50, 'customfield-3', customFieldHeader3, 'text', null, customField3, setCustomField3, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader4
          && (
            renderEdit(50, 'customfield-4', customFieldHeader4, 'text', null, customField4, setCustomField4, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader5
          && (
            renderEdit(50, 'customfield-5', customFieldHeader5, 'text', null, customField5, setCustomField5, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader6
          && (
            renderEdit(50, 'customfield-6', customFieldHeader6, 'text', null, customField6, setCustomField6, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader7
          && (
            renderEdit(50, 'customfield-7', customFieldHeader7, 'text', null, customField7, setCustomField7, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader8
          && (
            renderEdit(50, 'customfield-8', customFieldHeader8, 'text', null, customField8, setCustomField8, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader9
          && (
            renderEdit(50, 'customfield-9', customFieldHeader9, 'text', null, customField9, setCustomField9, controlStateEditable, controlStyle)
          )
        }
        {
          customFieldHeader10
          && (
            renderEdit(50, 'customfield-10', customFieldHeader10, 'text', null, customField10, setCustomField10, controlStateEditable, controlStyle)
          )
        }
      </div>
      <Footer onCancel={onCancel} saveEnabled={canSaveContinue} onSave={onSaveContinue} saveText="Continue" />
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default CertificateInformation;
